import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/I18nProvider";
import { BLOG_POSTS } from "@/lib/blogPosts";

export default function Blog() {
  const { lang, t } = useI18n();

  const overline = lang === "fr" ? "Insights" : lang === "es" ? "Insights" : lang === "pt" ? "Insights" : "Insights";
  const titleA = lang === "fr" ? "Comment construire," : lang === "es" ? "Cómo construir," : lang === "pt" ? "Como construir," : "How to build,";
  const titleB = lang === "fr" ? "lancer et monétiser une chaîne." : lang === "es" ? "lanzar y monetizar un canal." : lang === "pt" ? "lançar e monetizar um canal." : "launch and monetize a channel.";
  const subtitle = lang === "fr" ? "Tactiques, playbooks et chiffres concrets pour opérateurs OTT/IPTV. Mis à jour par notre équipe."
    : lang === "es" ? "Tácticas, playbooks y números concretos para operadores OTT/IPTV. Actualizado por nuestro equipo."
    : lang === "pt" ? "Táticas, playbooks e números concretos para operadores OTT/IPTV. Atualizado pela nossa equipa."
    : "Tactics, playbooks and concrete numbers for OTT/IPTV operators. Maintained by our team.";
  const readMore = lang === "fr" ? "Lire l'article" : lang === "es" ? "Leer el artículo" : lang === "pt" ? "Ler artigo" : "Read article";
  const minRead = lang === "fr" ? "min de lecture" : lang === "es" ? "min de lectura" : lang === "pt" ? "min de leitura" : "min read";
  const note = lang !== "en"
    ? (lang === "fr" ? "Articles disponibles en anglais — traductions en cours."
      : lang === "es" ? "Artículos disponibles en inglés — traducciones en curso."
      : "Artigos disponíveis em inglês — traduções em curso.")
    : null;

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="overline">{overline}</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] max-w-4xl text-white">
            {titleA} <span className="gradient-text">{titleB}</span>
          </h1>
          <p className="mt-8 text-base md:text-xl text-slate-200 max-w-2xl leading-relaxed">{subtitle}</p>
          {note && <p className="mt-3 text-xs text-slate-400">* {note}</p>}
        </div>
      </section>

      {/* Posts */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BLOG_POSTS.map((post, i) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-hover glass rounded-3xl overflow-hidden flex flex-col group"
                data-testid={`blog-card-${post.slug}`}
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="aspect-[16/10] overflow-hidden bg-[#1A2236] relative">
                    <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A2236] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm font-mono text-[10px] uppercase tracking-widest text-white border border-white/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <ArrowUpRight size={20} className="absolute top-4 right-4 text-white/80 group-hover:text-white" />
                  </div>
                </Link>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-[11px] font-mono text-slate-400 uppercase tracking-widest">
                    <span className="flex items-center gap-1.5"><Calendar size={12} /> {new Date(post.published_at).toLocaleDateString(lang === "en" ? "en-US" : lang, { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span className="flex items-center gap-1.5"><Clock size={12} /> {post.read_time} {minRead}</span>
                  </div>
                  <h2 className="font-display mt-4 text-xl md:text-2xl font-bold tracking-tight text-white leading-tight">
                    <Link to={`/blog/${post.slug}`} className="hover:text-[#1FA8FF] transition-colors">{post.title}</Link>
                  </h2>
                  <p className="mt-3 text-sm text-slate-200 leading-relaxed flex-1">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`} data-testid={`blog-read-${post.slug}`} className="mt-5 inline-flex items-center gap-2 font-semibold text-[#1FA8FF] hover:text-[#1FE0A8] transition-colors text-sm">
                    {readMore} <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 border-t border-white/10 bg-[#324567]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {lang === "fr" ? <>Prêt à <span className="gradient-text">lancer votre chaîne</span> ?</>
            : lang === "es" ? <>¿Listo para <span className="gradient-text">lanzar tu canal</span>?</>
            : lang === "pt" ? <>Pronto para <span className="gradient-text">lançar o seu canal</span>?</>
            : <>Ready to <span className="gradient-text">launch your channel</span>?</>}
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="blog-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="blog-cta-contact" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
